// FIX: Import missing React hooks and types.
import React, { useState, ChangeEvent, useRef, useEffect, FormEvent, useMemo } from 'react';
import PageHeader from '../PageHeader';
import { InventorySection, SavablePageProps, NewSignature } from '../../types';
import { API_URL, API_KEY_EN } from '../../user_constant';
import CryptoJS from 'crypto-js';

interface Schedule3PageProps extends SavablePageProps {
    onContinue: () => void;
}

// Main Section Wrapper
const SignatorySection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="bg-slate-50/70 p-6 rounded-xl border border-slate-200 mb-8 print:bg-transparent print:border-none print:p-0 print:mb-4 print:break-inside-avoid">
        <h3 className="text-xl font-semibold text-slate-800 mb-6 border-b border-slate-200 pb-4 print:text-lg print:mb-3 print:pb-2">{title}</h3>
        {children}
    </div>
);

// Form Field Component
const FormField: React.FC<{
    label: string;
    name: string;
    placeholder?: string;
    value?: string | number;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    readOnly?: boolean;
    highlight?: boolean;
    isPrintView?: boolean;
    isStaffInput?: boolean;
}> = ({ label, name, placeholder = '', value, onChange, readOnly = false, highlight = false, isPrintView = false, isStaffInput = true }) => {
    if (isPrintView) {
        return (
            <div className="print:mb-1">
                <label className="block text-sm font-semibold text-slate-700 mb-1 print:mb-0 print:text-xs print:text-slate-500">{label}</label>
                <div className="text-sm text-slate-900 p-2 min-h-[38px] bg-slate-50 rounded-md break-words print:p-0 print:min-h-0 print:bg-transparent print:border-b print:border-slate-300 print:rounded-none print:pb-1 print:leading-snug">{value || '-'}</div>
            </div>
        );
    }
    const baseBorder = isStaffInput ? 'border-brand-primary border-2 shadow-md' : 'border-slate-300 border shadow-sm hover:border-slate-400';
    const baseClasses = `block w-full px-4 py-2 ${baseBorder} rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/80 focus:border-brand-primary transition-all duration-200`;
    const statefulClasses = readOnly
        ? "bg-slate-100 text-slate-500 cursor-not-allowed border-none hover:border-none"
        : "bg-white text-slate-900 placeholder-slate-400";

    return (
        <div>
            <label htmlFor={name} className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
            <input
                id={name}
                name={name}
                type="text"
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                readOnly={readOnly}
                className={`${baseClasses} ${statefulClasses} ${highlight ? 'animate-pulse-highlight ring-2 ring-brand-primary/50' : ''}`}
            />
        </div>
    );
};

// New Date Field Component with "Now" button
const DateField: React.FC<{
    label: string;
    name: string;
    placeholder?: string;
    value?: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onSetNow: () => void;
    highlight?: boolean;
    isPrintView?: boolean;
    isStaffInput?: boolean;
}> = ({ label, name, placeholder, value, onChange, onSetNow, highlight = false, isPrintView = false, isStaffInput = true }) => {
    if (isPrintView) {
        return (
            <div className="print:mb-1">
                <label className="block text-sm font-semibold text-slate-700 mb-1 print:mb-0 print:text-xs print:text-slate-500">{label}</label>
                <div className="text-sm text-slate-900 p-2 min-h-[38px] bg-slate-50 rounded-md break-words print:p-0 print:min-h-0 print:bg-transparent print:border-b print:border-slate-300 print:rounded-none print:pb-1 print:leading-snug">
                    {value || '-'}
                </div>
            </div>
        );
    }
    
    return (
    <div>
        <label htmlFor={name} className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
        <div className="relative">
            <input
                id={name}
                name={name}
                type="text"
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                className={`block w-full px-4 py-2 bg-white text-slate-900 ${isStaffInput ? 'border-brand-primary border-2 shadow-md' : 'border border-slate-300 shadow-sm hover:border-slate-400'} rounded-lg placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/80 focus:border-brand-primary transition-all duration-200 ${highlight ? 'animate-pulse-highlight ring-2 ring-brand-primary/50' : ''}`}
            />
            <button
                type="button"
                onClick={onSetNow}
                className="absolute top-1/2 right-2 -translate-y-1/2 px-3 py-1 text-xs font-semibold text-brand-primary bg-brand-primary-light rounded-md hover:bg-brand-primary/20 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
            >
                Now
            </button>
        </div>
    </div>
    )
};


// Reusable component for modern signature pads
const SignaturePad: React.FC<{ 
    label: string; 
    signature: string | null;
    onSign: (dataUrl: string | null) => void;
    readOnly?: boolean;
    highlight?: boolean;
    isPrintView?: boolean;
}> = ({ label, signature, onSign, readOnly = false, highlight = false, isPrintView = false }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    
    if (isPrintView) {
        return (
            <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2 print:mb-1 print:text-xs print:text-slate-500">{label}</label>
                <div className="relative w-full h-32 bg-slate-50 border border-slate-300 rounded-lg flex items-center justify-center print:h-24 print:bg-transparent print:rounded-none">
                    {signature ? (
                        <img src={signature} alt={label} className="max-w-full max-h-full object-contain" />
                    ) : (
                        <span className="text-sm text-slate-500 font-medium">
                            [ Not Signed ]
                        </span>
                    )}
                </div>
            </div>
        );
    }


    const getCoords = (event: MouseEvent | TouchEvent) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        if (event instanceof MouseEvent) return { x: event.clientX - rect.left, y: event.clientY - rect.top };
        if (event.touches?.[0]) return { x: event.touches[0].clientX - rect.left, y: event.touches[0].clientY - rect.top };
        return { x: 0, y: 0 };
    };

    const startDrawing = (event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        if (readOnly) return;
        event.preventDefault();
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!ctx) return;
        const { x, y } = getCoords(event.nativeEvent);
        ctx.beginPath();
        ctx.moveTo(x, y);
        setIsDrawing(true);
    };

    const draw = (event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        if (readOnly || !isDrawing) return;
        event.preventDefault();
        const { x, y } = getCoords(event.nativeEvent);
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!ctx) return;
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const stopDrawing = () => {
        if (readOnly || !isDrawing) return;
        setIsDrawing(false);
        const canvas = canvasRef.current;
        if (canvas) {
            onSign(canvas.toDataURL('image/jpeg'));
        }
    };
    
    const clearCanvas = () => {
        if (readOnly) return;
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (ctx && canvas) {
            const rect = canvas.getBoundingClientRect();
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, rect.width, rect.height);
            onSign(null);
        }
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
        ctx.strokeStyle = '#374151';
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, rect.width, rect.height);

        if (signature) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, rect.width, rect.height);
                ctx.drawImage(img, 0, 0, rect.width, rect.height);
            }
            img.src = signature;
        }
    }, [signature]);

    return (
        <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
            <div 
                className={`relative w-full h-48 bg-white border-2 border-dashed border-slate-300 rounded-lg transition-colors duration-200 ${highlight ? 'animate-pulse-highlight' : ''} ${readOnly ? 'cursor-not-allowed bg-slate-50' : 'cursor-crosshair hover:border-brand-primary/80'}`}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <canvas
                    ref={canvasRef}
                    className="w-full h-full rounded-lg"
                    onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing}
                />
                {!signature && !readOnly && (
                    <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-slate-400 font-medium pointer-events-none">
                        Sign here
                    </span>
                )}
                {readOnly && signature && (
                    <div className="absolute top-2 right-2 flex items-center bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full z-10 pointer-events-none">
                        <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Signed
                    </div>
                )}
                {!readOnly && signature && isHovered && (
                     <button
                        type="button"
                        onClick={clearCanvas}
                        className="absolute top-2 right-2 p-1.5 bg-white/80 backdrop-blur-sm rounded-full text-slate-500 hover:bg-red-100 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors z-10"
                        aria-label="Clear signature"
                    >
                        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                           <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.58.22-2.365.468a.75.75 0 10.23 1.482l.149-.046a.75.75 0 01.701.043l2.25 1.5a.75.75 0 01.043.701l-.046.149a.75.75 0 101.482.23l.468-2.365v-.443A2.75 2.75 0 008.75 1zM3.75 8a.75.75 0 01.75.75v6.5a.75.75 0 01-1.5 0v-6.5A.75.75 0 013.75 8zM8 8a.75.75 0 01.75.75v6.5a.75.75 0 01-1.5 0v-6.5A.75.75 0 018 8zM12.25 8a.75.75 0 01.75.75v6.5a.75.75 0 01-1.5 0v-6.5a.75.75 0 01.75-.75zM16 8a.75.75 0 01.75.75v6.5a.75.75 0 01-1.5 0v-6.5A.75.75 0 0116 8z" clipRule="evenodd" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
};

const CustomCheckbox: React.FC<{
    label: string;
    checked: boolean;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    id: string;
    disabled?: boolean;
}> = ({ label, checked, onChange, id, disabled = false }) => (
    <label htmlFor={id} className={`flex items-center group p-2 rounded-md transition-colors ${disabled ? 'cursor-not-allowed opacity-70 print:p-0' : 'cursor-pointer hover:bg-slate-100'}`}>
        <input
            id={id}
            type="checkbox"
            checked={checked}
            onChange={onChange}
            className="sr-only peer"
            disabled={disabled}
        />
        <div className="w-5 h-5 mr-2.5 flex-shrink-0 border-2 border-slate-300 rounded-md group-hover:border-brand-primary peer-checked:bg-brand-primary peer-checked:border-brand-primary transition-all duration-200 flex items-center justify-center print:w-4 print:h-4 print:mr-1.5">
            <svg className="w-3.5 h-3.5 text-white transform opacity-0 scale-75 peer-checked:opacity-100 peer-checked:scale-100 transition-all duration-300 ease-in-out" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.5 12.75l6 6 9-13.5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        </div>
        <span className={`text-sm font-medium text-slate-600 group-hover:text-slate-900 ${checked && !disabled ? 'text-brand-primary' : ''} print:text-xs`}>
            {label}
        </span>
    </label>
);

const InventoryChecklist: React.FC<{
    items: InventorySection[];
    onItemChange: (sectionIndex: number, itemIndex: number, field: 'isCheckIn' | 'isCheckOut', value: boolean) => void;
    isClientView: boolean;
    isPrintView?: boolean;
}> = ({ items, onItemChange, isClientView, isPrintView = false }) => {
    return (
        <div className="space-y-8 print:space-y-4">
            {items.map((section, sectionIndex) => (
                <div key={sectionIndex} className="page-break-inside-avoid">
                    <h4 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b border-slate-200 print:text-base print:mb-2 print:pb-1">{section.title}</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 print:grid-cols-3 print:gap-2">
                        {section.inventories.map((item, itemIndex) => (
                            <div key={item.id || itemIndex} className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 transition-all duration-200 hover:shadow-md hover:border-brand-primary/40 print:shadow-none print:border-none print:p-0 print:bg-transparent">
                                <p className="font-semibold text-slate-800 truncate print:text-xs print:whitespace-normal" title={item.title}>
                                    {item.title}
                                </p>
                                <hr className="my-3 border-slate-200 print:hidden" />
                                <div className="flex justify-around items-center print:justify-start print:space-x-4">
                                    <CustomCheckbox
                                        id={`check-in-${sectionIndex}-${itemIndex}`}
                                        label="Check-in"
                                        checked={item.isCheckIn === 1}
                                        onChange={(e) => onItemChange(sectionIndex, itemIndex, 'isCheckIn', e.target.checked)}
                                        disabled={isClientView || isPrintView}
                                    />
                                    <div className="h-6 w-px bg-slate-200 print:hidden"></div>
                                    <CustomCheckbox
                                        id={`check-out-${sectionIndex}-${itemIndex}`}
                                        label="Check-out"
                                        checked={item.isCheckOut === 1}
                                        onChange={(e) => onItemChange(sectionIndex, itemIndex, 'isCheckOut', e.target.checked)}
                                        disabled={isClientView || isPrintView}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

const isFieldEmpty = (value: any): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (typeof value === 'number' && isNaN(value)) return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
};

function dataURLtoFile(dataurl: string, filename: string): File {
    const arr = dataurl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch) {
        throw new Error('Invalid data URL');
    }
    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
}


const Schedule3Page: React.FC<Schedule3PageProps> = ({ data, onUpdate, isSaving, isClientView, isPrintView = false, onContinue }) => {
    const findSignature = (type1: string, type2: string) => data.signatures.find(s => s.type === type1 || s.type === type2);

    const formatDateForDisplay = (isoString: string | null | undefined) => {
        if (!isoString) return '';
        const d = new Date(isoString);
        if (isNaN(d.getTime())) {
            return '';
        }
        return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
    };
    
    const formatDateForPayload = (displayDate: string) => {
        if (!displayDate) return null;
        const parts = displayDate.match(/(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})/);
        if (!parts) return null;
        const [_, day, month, year, hour, minute] = parts;
        return new Date(`${year}-${month}-${day}T${hour}:${minute}:00`).toISOString();
    };

    const [inventory, setInventory] = useState<InventorySection[]>(JSON.parse(JSON.stringify(data.items)));
    const [isUploadingSignatures, setIsUploadingSignatures] = useState(false);

    // Check-in States
    const [checkinDateTime, setCheckinDateTime] = useState('');
    const [checkinFullName, setCheckinFullName] = useState('');
    const [checkinPosition, setCheckinPosition] = useState('');
    const [checkinGuestSign, setCheckinGuestSign] = useState<string | null>(null);
    const [checkinStaffSign, setCheckinStaffSign] = useState<string | null>(null);
    const [displayCheckinGuestSign, setDisplayCheckinGuestSign] = useState<string | null>(null);
    const [displayCheckinStaffSign, setDisplayCheckinStaffSign] = useState<string | null>(null);

    // Check-out States
    const [checkoutDateTime, setCheckoutDateTime] = useState('');
    const [checkoutFullName, setCheckoutFullName] = useState('');
    const [checkoutPosition, setCheckoutPosition] = useState('');
    const [checkoutGuestSign, setCheckoutGuestSign] = useState<string | null>(null);
    const [checkoutStaffSign, setCheckoutStaffSign] = useState<string | null>(null);
    const [displayCheckoutGuestSign, setDisplayCheckoutGuestSign] = useState<string | null>(null);
    const [displayCheckoutStaffSign, setDisplayCheckoutStaffSign] = useState<string | null>(null);

    const [initialState, setInitialState] = useState(data);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const isStaffCheckinReady = !isFieldEmpty(checkinFullName) && !isFieldEmpty(checkinPosition) && !isFieldEmpty(checkinDateTime);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const isStaffCheckoutReady = !isFieldEmpty(checkoutFullName) && !isFieldEmpty(checkoutPosition) && !isFieldEmpty(checkoutDateTime);

     const decryptedCustomerId = useMemo(() => {
        if (!data.customer_id) return '';
        try {
            const encKey = "CareConcierge";
            const decrypted = CryptoJS.AES.decrypt(data.customer_id, encKey);
            return decrypted.toString(CryptoJS.enc.Utf8);
        } catch (error) {
            console.error("Failed to decrypt customer ID:", error);
            return 'Decryption Error';
        }
    }, [data.customer_id]);

    useEffect(() => {
        const fetchSignatureUrl = async (s3Url: string) => {
            const apiUrl = `${API_URL}/resident/attachment?url=${encodeURIComponent(s3Url)}`;
            try {
                const response = await fetch(apiUrl, {
                    method: 'GET',
                    headers: { 'x-api-key': API_KEY_EN },
                });
                if (!response.ok) return s3Url.startsWith('http') ? s3Url : null;
                const responseText = await response.text();
                let url: string | null = null;
                try {
                    const parsed = JSON.parse(responseText);
                    url = typeof parsed === 'string' ? parsed : parsed.url || parsed.downloadUrl;
                } catch {
                    url = responseText.startsWith('http') ? responseText : null;
                }
                return url;
            } catch {
                return s3Url.startsWith('http') ? s3Url : null;
            }
        };

        const signaturesMap = {
            guest_checkin_type: setDisplayCheckinGuestSign,
            staff_checkin_type: setDisplayCheckinStaffSign,
            guest_checkout_type: setDisplayCheckoutGuestSign,
            staff_checkout_type: setDisplayCheckoutStaffSign,
        };

        data.signatures.forEach(sig => {
            if (sig.type in signaturesMap && sig.signature_url_s3) {
                fetchSignatureUrl(sig.signature_url_s3).then(url => {
                    if(url) signaturesMap[sig.type as keyof typeof signaturesMap](url);
                });
            }
        });
    }, [data.signatures]);

    useEffect(() => {
        setInitialState(data);
        setInventory(JSON.parse(JSON.stringify(data.items)));

        const findSig = (type1: string, type2: string) => data.signatures.find(s => s.type === type1 || s.type === type2);
        const guestCheckin = findSig('checkin_guest', 'guest_checkin_type');
        const staffCheckin = findSig('checkin_staff', 'staff_checkin_type');
        const guestCheckout = findSig('checkout_guest', 'guest_checkout_type');
        const staffCheckout = findSig('checkout_staff', 'staff_checkout_type');

        setCheckinDateTime(formatDateForDisplay(staffCheckin?.date || guestCheckin?.date));
        setCheckinFullName(staffCheckin?.name || '');
        setCheckinPosition(staffCheckin?.position || '');

        setCheckoutDateTime(formatDateForDisplay(staffCheckout?.date || guestCheckout?.date));
        setCheckoutFullName(staffCheckout?.name || '');
        setCheckoutPosition(staffCheckout?.position || '');
    }, [data]);

    const handleInventoryChange = (sectionIndex: number, itemIndex: number, field: 'isCheckIn' | 'isCheckOut', value: boolean) => {
        setInventory(prev => {
            const newState = [...prev];
            newState[sectionIndex].inventories[itemIndex][field] = value ? 1 : 0;
            return newState;
        });
    };

    const handleSetNow = (setter: React.Dispatch<React.SetStateAction<string>>) => {
        const d = new Date();
        const str = `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
        setter(str);
    };

    const isDirty = useMemo(() => {
        const inventoryChanged = JSON.stringify(inventory) !== JSON.stringify(initialState.items);
        const signaturesChanged = 
            checkinGuestSign !== null || 
            checkinStaffSign !== null || 
            checkoutGuestSign !== null || 
            checkoutStaffSign !== null;
            
        const textFieldsChanged = 
            checkinFullName !== (findSignature('checkin_staff', 'staff_checkin_type')?.name || '') ||
            checkinPosition !== (findSignature('checkin_staff', 'staff_checkin_type')?.position || '') ||
            checkinDateTime !== formatDateForDisplay(findSignature('checkin_staff', 'staff_checkin_type')?.date || findSignature('checkin_guest', 'guest_checkin_type')?.date) ||
            checkoutFullName !== (findSignature('checkout_staff', 'staff_checkout_type')?.name || '') ||
            checkoutPosition !== (findSignature('checkout_staff', 'staff_checkout_type')?.position || '') ||
            checkoutDateTime !== formatDateForDisplay(findSignature('checkout_staff', 'staff_checkout_type')?.date || findSignature('checkout_guest', 'guest_checkout_type')?.date);

        return inventoryChanged || signaturesChanged || textFieldsChanged;
    }, [inventory, initialState, checkinGuestSign, checkinStaffSign, checkoutGuestSign, checkoutStaffSign, checkinFullName, checkinPosition, checkinDateTime, checkoutFullName, checkoutPosition, checkoutDateTime]);

    const uploadSignature = async (dataUrl: string, type: string): Promise<string | null> => {
         const LOG_PREFIX = `[Signature Upload - Schedule 3]`;
        console.log(`${LOG_PREFIX} Starting for type: ${type}`);
    
        if (!decryptedCustomerId || decryptedCustomerId === 'Decryption Error') {
            console.error(`${LOG_PREFIX} FAILED: Cannot upload signature without a valid customer ID.`);
            return null;
        }
    
        try {
            const filename = `${type}.jpg`;
            const prefix = `${decryptedCustomerId}/${type}/signatures`;
            const presignedUrlPayload = { payload: [{ prefix, filename }] };
            const presignedUrlApi = `${API_URL}/resident/attachment`;
            const presignedUrlResponse = await fetch(presignedUrlApi, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY_EN },
                body: JSON.stringify(presignedUrlPayload),
            });
            
            if (!presignedUrlResponse.ok) throw new Error('Failed to get pre-signed URL');
            
            const responseData = await presignedUrlResponse.json();
            const filePath = `${prefix}/${filename}`;
            const responseArray = responseData[filePath];
            const uploadInfo = Array.isArray(responseArray) && responseArray.length > 0 ? responseArray[0] : null;

            if (!uploadInfo) throw new Error('Invalid pre-signed URL response');
    
            const file = dataURLtoFile(dataUrl, filename);
            const uploadResponse = await fetch(uploadInfo.uploadUrl, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': file.type },
            });
            
            if (!uploadResponse.ok) throw new Error('S3 upload failed');
            return uploadInfo.downloadUrl;
        } catch (error) {
            console.error(`${LOG_PREFIX} FAILED`, error);
            return null;
        }
    };

    const performSave = async () => {
        setIsUploadingSignatures(true);
        const inventoryUpdates: any[] = [];
        inventory.forEach(section => {
            section.inventories.forEach(item => {
                const originalSection = initialState.items.find(s => s.title === section.title);
                const originalItem = originalSection?.inventories.find(i => 
                    (item.id && i.id === item.id) || 
                    (!item.id && i.title === item.title)
                );
                if (originalItem && (originalItem.isCheckIn !== item.isCheckIn || originalItem.isCheckOut !== item.isCheckOut)) {
                    const updatePayload: any = {
                        check_in: item.isCheckIn,
                        check_out: item.isCheckOut
                    };
                    
                    if (item.id) {
                        updatePayload.id = item.id;
                    } else {
                        updatePayload.name = item.title;
                    }
                    
                    inventoryUpdates.push(updatePayload);
                }
            });
        });

        const newSignatures: NewSignature[] = [];
        const signaturesToUpload = [
            { type: 'checkin_guest', legacyType: 'guest_checkin_type', data: checkinGuestSign, name: null, position: null, date: formatDateForPayload(checkinDateTime) },
            { type: 'checkin_staff', legacyType: 'staff_checkin_type', data: checkinStaffSign, name: checkinFullName, position: checkinPosition, date: formatDateForPayload(checkinDateTime) },
            { type: 'checkout_guest', legacyType: 'guest_checkout_type', data: checkoutGuestSign, name: null, position: null, date: formatDateForPayload(checkoutDateTime) },
            { type: 'checkout_staff', legacyType: 'staff_checkout_type', data: checkoutStaffSign, name: checkoutFullName, position: checkoutPosition, date: formatDateForPayload(checkoutDateTime) },
        ];

        const uploadPromises = signaturesToUpload.map(async (sig) => {
             const existing = findSignature(sig.legacyType, sig.type);
             if (sig.data && sig.data.startsWith('data:image')) {
                const url = await uploadSignature(sig.data, sig.type);
                if (url) {
                    const newSigPayload: NewSignature = {
                        type: sig.type,
                        signature_url_s3: url,
                        name: sig.name,
                        position: sig.position,
                        date: sig.date || new Date().toISOString(),
                    };
                    if (existing && existing.id) newSigPayload.id = existing.id;
                    newSignatures.push(newSigPayload);
                }
            } else if (sig.name || sig.position || sig.date) {
                 const dateChanged = existing?.date !== sig.date;
                 const nameChanged = existing?.name !== sig.name;
                 const posChanged = existing?.position !== sig.position;
                 if (dateChanged || nameChanged || posChanged) {
                     const updateSigPayload: NewSignature = {
                         type: sig.type,
                         signature_url_s3: existing?.signature_url_s3 || "",
                         name: sig.name,
                         position: sig.position,
                         date: sig.date
                     };
                     if (existing && existing.id) updateSigPayload.id = existing.id;
                     newSignatures.push(updateSigPayload);
                 }
            }
        });

        await Promise.all(uploadPromises);

        const payload: any = {};
        if (inventoryUpdates.length > 0) payload.items = inventoryUpdates;
        if (newSignatures.length > 0) payload.signatures = newSignatures;

        if (Object.keys(payload).length > 0) {
             await onUpdate(payload);
             setCheckinGuestSign(null);
             setCheckinStaffSign(null);
             setCheckoutGuestSign(null);
             setCheckoutStaffSign(null);
        }
        setIsUploadingSignatures(false);
    };

    const handleSaveSubmit = async (e: FormEvent) => {
        e.preventDefault();
        try { await performSave(); } catch (error) {}
    };

    const handleNextClick = async () => {
        try {
            if (isDirty) await performSave();
            onContinue();
        } catch (error) {}
    };

    const SaveButton = (
        <button type="submit" disabled={isSaving || isUploadingSignatures || !isDirty} className="w-full sm:w-auto px-8 py-3 bg-slate-600 text-white font-bold rounded-lg shadow-md hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">
            {isSaving || isUploadingSignatures ? 'Saving...' : 'Save'}
        </button>
    );

    const NextButton = (
        <button type="button" onClick={handleNextClick} disabled={isSaving || isUploadingSignatures} className="w-full sm:w-auto px-8 py-3 bg-brand-primary text-white font-bold rounded-lg shadow-md hover:bg-brand-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-all duration-200 flex items-center justify-center">
            Next
        </button>
    );
    const actionButtons = <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">{SaveButton}{NextButton}</div>;

    const commonPrintProps = { isPrintView };

    return (
        <div className="animate-fade-in">
             <form className="space-y-4" onSubmit={handleSaveSubmit}>
                <PageHeader 
                    title="Service Agreement - Schedule 3" 
                    subtitle="Inventory Checklist & Handover"
                    action={actionButtons}
                    isPrintView={isPrintView}
                />

                <InventoryChecklist 
                    items={inventory} 
                    onItemChange={handleInventoryChange} 
                    isClientView={isClientView}
                    isPrintView={isPrintView}
                />

                <SignatorySection title="Handover (Check-In)">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 mt-6 print:gap-y-4">
                         <div className="space-y-6 print:space-y-2">
                             <h4 className="text-base font-semibold text-slate-600">Resident / Surety</h4>
                             <SignaturePad 
                                {...commonPrintProps}
                                label="Signature"
                                signature={checkinGuestSign || displayCheckinGuestSign}
                                onSign={setCheckinGuestSign}
                                readOnly={!!displayCheckinGuestSign || isClientView || isPrintView}
                             />
                         </div>
                         <div className="space-y-6 print:space-y-2">
                             <h4 className="text-base font-semibold text-slate-600">Staff In-Charge</h4>
                             <SignaturePad 
                                {...commonPrintProps}
                                label="Signature"
                                signature={checkinStaffSign || displayCheckinStaffSign}
                                onSign={setCheckinStaffSign}
                                readOnly={!!displayCheckinStaffSign || isClientView || isPrintView || (!isStaffCheckinReady && !isClientView)}
                                highlight={!isClientView && isFieldEmpty(checkinStaffSign || displayCheckinStaffSign)}
                             />
                              {!isStaffCheckinReady && !displayCheckinStaffSign && !isClientView && !isPrintView && (
                                  <p className="text-xs text-red-500 -mt-4 px-1">
                                      Please fill in Full Name, Position, and Date & Time before signing.
                                  </p>
                              )}
                             <FormField {...commonPrintProps} label="Full Name" name="checkinName" value={checkinFullName} onChange={e => setCheckinFullName(e.target.value)} readOnly={isClientView || isPrintView} highlight={!isClientView && isFieldEmpty(checkinFullName)} />
                             <FormField {...commonPrintProps} label="Position" name="checkinPosition" value={checkinPosition} onChange={e => setCheckinPosition(e.target.value)} readOnly={isClientView || isPrintView} highlight={!isClientView && isFieldEmpty(checkinPosition)} />
                             <DateField {...commonPrintProps} label="Date & Time" name="checkinDateTime" placeholder="DD/MM/YYYY HH:MM" value={checkinDateTime} onChange={e => setCheckinDateTime(e.target.value)} onSetNow={() => handleSetNow(setCheckinDateTime)} highlight={!isClientView && isFieldEmpty(checkinDateTime)} />
                         </div>
                    </div>
                </SignatorySection>
                
                <SignatorySection title="Handover (Check-Out)">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 mt-6 print:gap-y-4">
                         <div className="space-y-6 print:space-y-2">
                             <h4 className="text-base font-semibold text-slate-600">Resident / Surety</h4>
                             <SignaturePad 
                                {...commonPrintProps}
                                label="Signature"
                                signature={checkoutGuestSign || displayCheckoutGuestSign}
                                onSign={setCheckoutGuestSign}
                                readOnly={!!displayCheckoutGuestSign || isClientView || isPrintView}
                             />
                         </div>
                         <div className="space-y-6 print:space-y-2">
                             <h4 className="text-base font-semibold text-slate-600">Staff In-Charge</h4>
                             <SignaturePad 
                                {...commonPrintProps}
                                label="Signature"
                                signature={checkoutStaffSign || displayCheckoutStaffSign}
                                onSign={setCheckoutStaffSign}
                                readOnly={!!displayCheckoutStaffSign || isClientView || isPrintView || (!isStaffCheckoutReady && !isClientView)}
                                highlight={!isClientView && isFieldEmpty(checkoutStaffSign || displayCheckoutStaffSign)}
                             />
                              {!isStaffCheckoutReady && !displayCheckoutStaffSign && !isClientView && !isPrintView && (
                                  <p className="text-xs text-red-500 -mt-4 px-1">
                                      Please fill in Full Name, Position, and Date & Time before signing.
                                  </p>
                              )}
                             <FormField {...commonPrintProps} label="Full Name" name="checkoutName" value={checkoutFullName} onChange={e => setCheckoutFullName(e.target.value)} readOnly={isClientView || isPrintView} highlight={!isClientView && isFieldEmpty(checkoutFullName)} />
                             <FormField {...commonPrintProps} label="Position" name="checkoutPosition" value={checkoutPosition} onChange={e => setCheckoutPosition(e.target.value)} readOnly={isClientView || isPrintView} highlight={!isClientView && isFieldEmpty(checkoutPosition)} />
                             <DateField {...commonPrintProps} label="Date & Time" name="checkoutDateTime" placeholder="DD/MM/YYYY HH:MM" value={checkoutDateTime} onChange={e => setCheckoutDateTime(e.target.value)} onSetNow={() => handleSetNow(setCheckoutDateTime)} highlight={!isClientView && isFieldEmpty(checkoutDateTime)} />
                         </div>
                    </div>
                </SignatorySection>

                {!isPrintView && (
                    <div className="pt-6 flex justify-end gap-4">
                        {actionButtons}
                    </div>
                )}
             </form>
        </div>
    );
}

export default Schedule3Page;